import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { pageColors } from "../mock";
import { ArrowLeft, Check, X, Shield, User as UserIcon, Flag, Trash2, Ban, UserX, Package, Music as MusicIcon, ShoppingBag, Palette, Eye, Shirt, DollarSign, Search, KeyRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { current, token, refreshUsers } = useAuth();
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [applications, setApplications] = useState([]);
  const [countAccess, setCountAccess] = useState([]);
  const [shirtClaims, setShirtClaims] = useState([]);
  const [requestsData, setRequestsData] = useState({ studio: [], consulting: [], artshow: [], photoshoot: [], photography: [], svcconsult: [], website: [], graphic: [], fashiondesign: [], gamedev: [], gamedevteam: [], modelcasting: [], preorders: [] });
  const [providers, setProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [svcDraft, setSvcDraft] = useState({ user_id: "", service_type: "photography", price_amount: "", price_unit: "hour", booking_fee: "" });
  const [memberSearch, setMemberSearch] = useState("");
  const [activity, setActivity] = useState(null);
  const [pageScopes, setPageScopes] = useState([]);
  const [rolesDraft, setRolesDraft] = useState({}); // user_id -> Set(pages)
  const [tab, setTab] = useState("members");
  const [appFilter, setAppFilter] = useState("all"); // all | music | brand | art
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [bodegaItems, setBodegaItems] = useState([]);
  const [bodegaProducts, setBodegaProducts] = useState([]);
  const [newPiece, setNewPiece] = useState({ name: "", price: "", size_label: "", description: "" });
  const [pieceFiles, setPieceFiles] = useState([]);
  const [pieceBusy, setPieceBusy] = useState(false);
  const [pieceErr, setPieceErr] = useState("");
  const [fileInputKey, setFileInputKey] = useState(0);

  const parseDetail = (d, fallback) => {
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => `${(e.loc || []).slice(-1)[0]}: ${e.msg}`).join("; ");
    return fallback;
  };

  const loadBodega = useCallback(() => {
    authFetch("/api/admin/bodega").then((r) => r.json()).then((d) => setBodegaItems(d.items || [])).catch(() => {});
    authFetch("/api/bodega/products").then((r) => r.json()).then((d) => setBodegaProducts(d.products || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (tab !== "bodega") return;
    loadBodega();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const createPiece = async () => {
    if (!newPiece.name.trim() || newPiece.price === "") { setPieceErr("Name and price are required."); return; }
    setPieceBusy(true);
    setPieceErr("");
    try {
      const photos = [];
      for (const f of pieceFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await fetch(`${API}/api/uploads`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
          body: fd,
        });
        const ud = await up.json();
        if (!up.ok) throw new Error(ud.detail || "Photo upload failed");
        photos.push(ud.url);
      }
      const res = await authFetch("/api/admin/bodega-products", {
        method: "POST",
        body: JSON.stringify({ ...newPiece, price: Number(newPiece.price), photos }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(parseDetail(d.detail, "Couldn't create the piece."));
      setNewPiece({ name: "", price: "", size_label: "", description: "" });
      setPieceFiles([]);
      setFileInputKey((k) => k + 1);
      loadBodega();
    } catch (e) {
      setPieceErr(e.message || "Something went wrong.");
    }
    setPieceBusy(false);
  };

  const togglePieceSold = async (id, sold) => {
    const res = await authFetch(`/api/admin/bodega/${id}`, { method: "PATCH", body: JSON.stringify({ sold: !sold }) });
    if (res.ok) loadBodega();
    else alert("Couldn't update that piece — try again.");
  };

  const deletePiece = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the bodega? Buyers won't be able to purchase it anymore.`)) return;
    const res = await authFetch(`/api/admin/bodega-products/${p.id}`, { method: "DELETE" });
    if (!res.ok) alert("Couldn't remove that piece — try again.");
    loadBodega();
  };

  const toggleSold = async (item) => {
    const res = await authFetch(`/api/admin/bodega/${item.id}`, {
      method: "PATCH",
      body: JSON.stringify({ sold: !item.sold }),
    });
    if (res.ok) {
      setBodegaItems((list) => list.map((b) => (b.id === item.id ? { ...b, sold: !item.sold } : b)));
    }
  };
  const [resetSent, setResetSent] = useState({});

  const sendResetLink = async (u) => {
    setResetSent((p) => ({ ...p, [u.id]: { status: "busy" } }));
    try {
      const res = await authFetch(`/api/admin/users/${u.id}/send-reset-link`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setResetSent((p) => ({ ...p, [u.id]: { status: "ok", emailed: !!data.emailed, email: data.email, link: data.link } }));
      } else {
        setResetSent((p) => ({ ...p, [u.id]: { status: "error" } }));
      }
    } catch {
      setResetSent((p) => ({ ...p, [u.id]: { status: "error" } }));
    }
  };

  const copyResetLink = async (id, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setResetSent((p) => ({ ...p, [id]: { ...p[id], copied: true } }));
      setTimeout(() => setResetSent((p) => ({ ...p, [id]: { ...p[id], copied: false } })), 2000);
    } catch {
      window.prompt("Copy this reset link:", link);
    }
  };

  useEffect(() => { document.title = "TMM|ADMIN"; }, []);

  const authFetch = useCallback(async (path, opts = {}) => {
    return fetch(`${API}${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opts.headers || {}),
      },
      credentials: "include",
    });
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [uRes, rRes, aRes, caRes, psRes] = await Promise.all([
        authFetch("/api/admin/users"),
        authFetch("/api/admin/reports"),
        authFetch("/api/applications"),
        authFetch("/api/admin/count-access"),
        authFetch("/api/admin/page-roles/available"),
      ]);
      const uData = await uRes.json();
      const rData = await rRes.json();
      const aData = await aRes.json();
      const caData = await caRes.json();
      const psData = await psRes.json();
      if (!uRes.ok) throw new Error(uData.detail || "Failed to load users");
      if (!rRes.ok) throw new Error(rData.detail || "Failed to load reports");
      setUsers(uData.users || []);
      setReports(rData.reports || []);
      setApplications(aData.applications || []);
      setCountAccess(caData.requests || []);
      setPageScopes(psData.pages || []);
      // Super-admin only — ignore a 403 silently.
      try {
        const sRes = await authFetch("/api/admin/shirt-claims");
        if (sRes.ok) {
          const sData = await sRes.json();
          setShirtClaims(sData.claims || []);
        }
      } catch { /* non-super admins simply don't see the tab */ }
      try {
        const qRes = await authFetch("/api/admin/requests");
        if (qRes.ok) setRequestsData(await qRes.json());
      } catch { /* ignore */ }
      try {
        const pRes = await authFetch("/api/admin/service-providers");
        if (pRes.ok) {
          const pData = await pRes.json();
          setProviders(pData.providers || []);
          setProviderTypes(pData.types || []);
        }
      } catch { /* ignore */ }
      try {
        const actRes = await authFetch("/api/admin/activity");
        if (actRes.ok) setActivity(await actRes.json());
      } catch { /* not a website manager */ }
      // Seed the draft with each admin's currently-assigned pages.
      const draft = {};
      (uData.users || []).forEach((u) => {
        draft[u.id] = new Set(u.page_admin_of || []);
      });
      setRolesDraft(draft);
    } catch (e) {
      setErr(e.message || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const decideRequest = async (kind, id, action) => {
    const body = { action };
    if (action === "rescheduled") {
      const nd = window.prompt("New date (YYYY-MM-DD)");
      if (!nd) return;
      const nt = window.prompt("New time (HH:MM)");
      if (!nt) return;
      body.new_date = nd;
      body.new_time = nt;
    }
    await authFetch(`/api/admin/requests/${kind}/${id}`, { method: "PATCH", body: JSON.stringify(body) });
    load();
  };

  const addProvider = async () => {
    if (!svcDraft.user_id || svcDraft.price_amount === "") return;
    const res = await authFetch("/api/admin/service-providers", {
      method: "POST",
      body: JSON.stringify({
        ...svcDraft,
        price_amount: Number(svcDraft.price_amount),
        booking_fee: svcDraft.booking_fee === "" ? null : Number(svcDraft.booking_fee),
      }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(typeof d.detail === "string" ? d.detail : "Couldn't assign — booking fee must be at least $0.50 (or blank).");
      return;
    }
    setSvcDraft({ ...svcDraft, user_id: "", price_amount: "", booking_fee: "" });
    load();
  };

  const removeProvider = async (pid) => {
    await authFetch(`/api/admin/service-providers/${pid}`, { method: "DELETE" });
    load();
  };

  const setWebsiteManager = async (userId, enabled) => {
    await authFetch(`/api/admin/users/${userId}/website-manager`, { method: "POST", body: JSON.stringify({ enabled }) });
    load();
  };

  useEffect(() => {
    if (current?.is_admin) load();
  }, [current, load]);

  if (!current) {
    return (
      <>
        <Header hoverColor={pageColors.contact} compact={false} />
        <div className="gated-page">
          <h1>Sign in</h1>
          <p>You need to sign in to use the admin panel.</p>
          <button className="btn-solid" onClick={() => navigate("/members")}>Go to Members</button>
        </div>
      </>
    );
  }
  if (!current.is_admin) {
    return (
      <>
        <Header hoverColor={pageColors.contact} compact={false} />
        <div className="gated-page">
          <h1>Admins only</h1>
          <p>This page is restricted to Monde Mania admins.</p>
          <button className="btn-solid" onClick={() => navigate("/members")}>Back to Members</button>
        </div>
      </>
    );
  }

  const togglePageRole = (userId, page) => {
    setRolesDraft((prev) => {
      const nextSet = new Set(prev[userId] || []);
      if (nextSet.has(page)) nextSet.delete(page);
      else nextSet.add(page);
      return { ...prev, [userId]: nextSet };
    });
  };

  const savePageRoles = async (userId) => {
    const pages = Array.from(rolesDraft[userId] || []);
    setBusyId(userId);
    try {
      const r = await authFetch(`/api/admin/users/${userId}/page-roles`, {
        method: "PATCH",
        body: JSON.stringify({ pages }),
      });
      const data = await r.json();
      if (!r.ok) {
        alert(data.detail || "Save failed");
      } else {
        await load();
      }
    } finally {
      setBusyId(null);
    }
  };

  const act = async (id, path, method = "POST", body, kind = "users") => {
    setBusyId(id);
    try {
      const suffix = path ? `/${path}` : "";
      const base = kind === "reports"
        ? `/api/admin/reports/${id}${suffix}`
        : kind === "applications"
        ? `/api/applications/${id}${suffix}`
        : kind === "count-access"
        ? `/api/admin/count-access/${id}${suffix}`
        : `/api/admin/users/${id}${suffix}`;
      const r = await authFetch(base, {
        method,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        alert(data.detail || "Action failed");
      } else {
        await load();
        refreshUsers();
      }
    } finally {
      setBusyId(null);
    }
  };

  const matchesSearch = (u) => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  };
  const pending = users.filter((u) => u.status === "pending" && matchesSearch(u));
  const approved = users.filter((u) => u.status !== "pending" && matchesSearch(u));

  return (
    <>
      <Header hoverColor={pageColors.contact} compact={false} />
      <div className="feed-page admin-page">
        <button
          type="button"
          className="feed-back"
          onClick={() => navigate("/members")}
          data-testid="admin-back-btn"
        >
          <ArrowLeft size={16} /> Back to Members
        </button>

        <header className="feed-hero" style={{ textAlign: "left" }}>
          <h1 className="feed-title">ADMIN</h1>
          <p className="feed-sub">
            Approve new signups, and promote or demote members. Only accounts in
            <code style={{ margin: "0 4px" }}>ADMIN_EMAILS</code> or with
            role=admin can see this page.
          </p>
        </header>

        {err && <div className="auth-error" data-testid="admin-error">{err}</div>}
        {loading && <div className="feed-empty">Loading…</div>}

        <div className="admin-tab-select-wrap" data-testid="admin-tab-select-wrap">
          <label className="req-label" htmlFor="admin-tab-select">Admin feature</label>
          <select
            id="admin-tab-select"
            className="req-input admin-tab-select"
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            data-testid="admin-tab-select"
          >
            {[
              { v: "members", label: `Members (${pending.length} pending)` },
              { v: "reports", label: `Reports (${reports.length})` },
              { v: "applications", label: `Applications (${applications.filter(a => a.status === "pending").length} pending)` },
              { v: "access", label: `Count access (${countAccess.filter(r => r.status === "pending").length} pending)` },
              ...(current?.is_super_admin ? [{ v: "shirt", label: `THE SHIRT (${shirtClaims.length})` }] : []),
              { v: "requests", label: `Requests (${["studio", "consulting", "photoshoot", "photography", "svcconsult", "website", "graphic", "fashiondesign", "modelcasting", "gamedev", "gamedevteam"].reduce((n, k) => n + (requestsData[k] || []).filter(r => r.status === "pending").length, 0)} pending)` },
              { v: "services", label: `Services — bookable members (${providers.length})` },
              { v: "bodega", label: "MJ's Bodega items" },
              { v: "casting", label: `Casting gallery (${(requestsData.modelcasting || []).filter(r => r.status === "approved").length})` },
              ...(activity ? [{ v: "activity", label: "Activity feed" }] : []),
            ].map((o) => (
              <option key={o.v} value={o.v}>{o.label}</option>
            ))}
          </select>
        </div>

        {tab === "members" && (
        <>
        <div className="admin-member-search" data-testid="admin-member-search-wrap">
          <Search size={15} className="admin-search-icon" />
          <input
            className="req-input"
            type="search"
            placeholder="Search members by name or email…"
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            data-testid="admin-member-search"
          />
          {memberSearch && (
            <button type="button" className="admin-search-clear" onClick={() => setMemberSearch("")} data-testid="admin-member-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        <section className="admin-section">
          <h2 className="admin-section-title">Pending signups ({pending.length})</h2>
          {pending.length === 0 ? (
            <div className="feed-empty">{memberSearch ? "No pending signups match your search." : "No one's waiting — inbox is clear."}</div>
          ) : (
            <div className="admin-list">
              {pending.map((u) => (
                <div key={u.id} className="admin-row" data-testid={`admin-pending-${u.id}`}>
                  <div className="admin-user">
                    <div className="admin-avatar">
                      {u.avatar || u.picture ? (
                        <img src={u.avatar || u.picture} alt="" />
                      ) : (
                        <div className="admin-avatar-fallback">{(u.name || u.email)[0]?.toUpperCase()}</div>
                      )}
                    </div>
                    <div>
                      <div className="admin-name">{u.name}</div>
                      <div className="admin-meta">{u.email} · {u.expertise || "member"}</div>
                      {u.invited_by_name && (
                        <div className="admin-meta admin-invited-by" data-testid={`admin-invited-by-${u.id}`}>
                          Invited by <b>{u.invited_by_name}</b>
                        </div>
                      )}
                      {u.intro && (
                        <div className="admin-intro" data-testid={`admin-intro-${u.id}`}>
                          <span className="admin-intro-label">Intro</span>
                          <span>{u.intro}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="admin-actions">
                    <button
                      className="btn-solid"
                      disabled={busyId === u.id}
                      onClick={() => act(u.id, "approve")}
                      data-testid={`admin-approve-${u.id}`}
                    >
                      <Check size={14} /> Approve
                    </button>
                    <button
                      className="btn-ghost btn-danger"
                      disabled={busyId === u.id}
                      onClick={() => {
                        if (window.confirm(`Reject ${u.email}? Their account will be deleted.`)) {
                          act(u.id, "reject");
                        }
                      }}
                      data-testid={`admin-reject-${u.id}`}
                    >
                      <X size={14} /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">Approved members ({approved.length})</h2>
          <div className="admin-list">
            {approved.map((u) => (
              <div key={u.id} className="admin-row" data-testid={`admin-user-${u.id}`}>
                <div className="admin-user">
                  <div className="admin-avatar">
                    {u.avatar || u.picture ? (
                      <img src={u.avatar || u.picture} alt="" />
                    ) : (
                      <div className="admin-avatar-fallback">{(u.name || u.email)[0]?.toUpperCase()}</div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="admin-name">
                      {u.name}{" "}
                      {u.is_super_admin ? (
                        <span className="admin-badge admin-badge-super" data-testid={`admin-super-badge-${u.id}`}>
                          <Shield size={12} /> top admin
                        </span>
                      ) : u.is_admin ? (
                        <span className="admin-badge">
                          <Shield size={12} /> admin
                        </span>
                      ) : null}
                      {(u.page_admin_of || []).length > 0 && !u.is_admin && (
                        <span className="admin-badge admin-badge-page" data-testid={`admin-page-badge-${u.id}`}>
                          {(u.page_admin_of || []).join(", ")}
                        </span>
                      )}
                    </div>
                    <div className="admin-meta">{u.email} · {u.expertise || "member"}</div>
                    {current?.is_super_admin && !u.is_super_admin && (
                      <div className="page-roles-editor" data-testid={`page-roles-${u.id}`}>
                        <div className="page-roles-label">Page roles</div>
                        <div className="page-roles-row">
                          {pageScopes.map((p) => {
                            const on = (rolesDraft[u.id] || new Set()).has(p);
                            return (
                              <label
                                key={p}
                                className={`role-chip${on ? " active" : ""}`}
                                data-testid={`page-role-${u.id}-${p}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => togglePageRole(u.id, p)}
                                />
                                {p}
                              </label>
                            );
                          })}
                          <button
                            type="button"
                            className="btn-solid page-roles-save"
                            disabled={busyId === u.id}
                            onClick={() => savePageRoles(u.id)}
                            data-testid={`page-roles-save-${u.id}`}
                          >
                            {busyId === u.id ? "…" : "Save"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="admin-actions">
                  {u.is_admin ? (
                    <button
                      className="btn-ghost"
                      disabled={busyId === u.id || u.email === current.email || u.is_super_admin}
                      title={u.is_super_admin ? "The top admin can't be demoted" : u.email === current.email ? "You can't demote yourself" : ""}
                      onClick={() => act(u.id, "role", "POST", { role: "member" })}
                      data-testid={`admin-demote-${u.id}`}
                    >
                      <UserIcon size={14} /> Demote to member
                    </button>
                  ) : (
                    <button
                      className="btn-solid"
                      disabled={busyId === u.id || !current?.is_super_admin}
                      title={!current?.is_super_admin ? "Only the top admin can promote members" : ""}
                      onClick={() => act(u.id, "role", "POST", { role: "admin" })}
                      data-testid={`admin-promote-${u.id}`}
                    >
                      <Shield size={14} /> Make admin
                    </button>
                  )}
                  {!u.is_admin && (
                    <button
                      className="btn-ghost btn-danger"
                      disabled={busyId === u.id}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Kick ${u.email} out? This deletes their account and everything they've posted. Cannot be undone.`
                          )
                        ) {
                          act(u.id, "", "DELETE");
                        }
                      }}
                      data-testid={`admin-kick-${u.id}`}
                    >
                      <UserX size={14} /> Kick out
                    </button>
                  )}
                  <button
                    className="btn-ghost"
                    disabled={resetSent[u.id]?.status === "busy"}
                    onClick={() => sendResetLink(u)}
                    data-testid={`admin-reset-link-${u.id}`}
                  >
                    <KeyRound size={14} />{" "}
                    {resetSent[u.id]?.status === "busy"
                      ? "Sending…"
                      : resetSent[u.id]?.status === "ok"
                      ? "Send again"
                      : resetSent[u.id]?.status === "error"
                      ? "Couldn't send — retry"
                      : "Send reset link"}
                  </button>
                  {resetSent[u.id]?.status === "ok" && (
                    <div className="reset-link-box" data-testid={`reset-link-box-${u.id}`}>
                      {resetSent[u.id].emailed ? (
                        <div className="reset-link-status ok">
                          <Check size={13} /> Emailed to <strong>{resetSent[u.id].email}</strong> (their signup email)
                        </div>
                      ) : (
                        <div className="reset-link-status warn" data-testid={`reset-link-warn-${u.id}`}>
                          <X size={13} /> Email to <strong>{resetSent[u.id].email}</strong> couldn't be delivered — copy the link below and send it to them yourself.
                        </div>
                      )}
                      <div className="reset-link-row">
                        <a
                          href={resetSent[u.id].link}
                          target="_blank"
                          rel="noreferrer"
                          className="reset-link-url"
                          data-testid={`reset-link-url-${u.id}`}
                        >
                          {resetSent[u.id].link}
                        </a>
                        <button
                          type="button"
                          className="btn-mini"
                          onClick={() => copyResetLink(u.id, resetSent[u.id].link)}
                          data-testid={`reset-link-copy-${u.id}`}
                        >
                          {resetSent[u.id].copied ? "Copied!" : "Copy link"}
                        </button>
                      </div>
                      <div className="reset-link-note">Works once, expires in 24 hours.</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
        </>
        )}

        {tab === "bodega" && (
          <section className="admin-section" data-testid="admin-bodega-section">
            <h2 className="admin-section-title">MJ's Bodega — one-of-one pieces</h2>
            <p className="gd-upload-note">Mark a piece SOLD to lock it: the product page shows a SOLD badge, the add-to-cart button disables, and Stripe checkout rejects it. Pieces also auto-mark SOLD when a checkout for them gets paid.</p>
            <div className="admin-list">
              {bodegaItems.map((b) => (
                <div className="admin-row" key={b.id} data-testid={`bodega-row-${b.id}`}>
                  <div className="admin-row-main">
                    <div className="admin-name">{b.name}</div>
                    <div className="admin-meta">{b.sold ? "SOLD — buyers can't check out" : "Available"}</div>
                  </div>
                  <div className="admin-actions">
                    <button
                      className={`btn-mini ${b.sold ? "" : "approve"}`}
                      onClick={() => toggleSold(b)}
                      data-testid={`bodega-toggle-${b.id}`}
                    >
                      {b.sold ? "Mark available" : "Mark SOLD"}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="admin-section-title" style={{ marginTop: "1.6rem" }}>Add a new piece</h3>
            <p className="gd-upload-note">Creates the Stripe product automatically — it shows up on MJ's Bodega under "The racks" instantly.</p>
            <div className="bodega-new-form" data-testid="bodega-new-form">
              <input className="req-input" placeholder="Piece name" value={newPiece.name}
                onChange={(e) => setNewPiece({ ...newPiece, name: e.target.value })} data-testid="bodega-new-name" />
              <input className="req-input" type="number" min="0.5" step="0.01" placeholder="Price $" value={newPiece.price}
                onChange={(e) => setNewPiece({ ...newPiece, price: e.target.value })} data-testid="bodega-new-price" />
              <input className="req-input" placeholder="Size label (optional, e.g. 10.5 or L)" value={newPiece.size_label}
                onChange={(e) => setNewPiece({ ...newPiece, size_label: e.target.value })} data-testid="bodega-new-size" />
              <input className="req-input" placeholder="Short description (optional)" value={newPiece.description}
                onChange={(e) => setNewPiece({ ...newPiece, description: e.target.value })} data-testid="bodega-new-desc" />
              <label className="gd-upload-note bodega-new-photos-label">
                Photos (up to 8):
                <input key={fileInputKey} type="file" accept="image/*" multiple
                  onChange={(e) => setPieceFiles(Array.from(e.target.files || []).slice(0, 8))}
                  data-testid="bodega-new-photos" />
              </label>
              {pieceFiles.length > 0 && <div className="gd-upload-note">{pieceFiles.length} photo(s) ready to upload</div>}
              {pieceErr && <div className="auth-error" data-testid="bodega-new-error">{pieceErr}</div>}
              <button className="btn-solid" disabled={pieceBusy} onClick={createPiece} data-testid="bodega-new-submit">
                {pieceBusy ? "Creating…" : "Add piece"}
              </button>
            </div>

            {bodegaProducts.length > 0 && (
              <>
                <h3 className="admin-section-title" style={{ marginTop: "1.6rem" }}>Your added pieces ({bodegaProducts.length})</h3>
                <div className="admin-list">
                  {bodegaProducts.map((p) => (
                    <div className="admin-row" key={p.id} data-testid={`bodega-prod-row-${p.id}`}>
                      <div className="admin-row-main">
                        <div className="admin-name">{p.name}{p.size_label ? ` — ${p.size_label}` : ""}</div>
                        <div className="admin-meta">
                          ${Number(p.price).toLocaleString()} · {(p.photos || []).length} photo(s) · {p.sold ? "SOLD" : "Available"}
                        </div>
                      </div>
                      <div className="admin-actions">
                        <button className={`btn-mini ${p.sold ? "" : "approve"}`}
                          onClick={() => togglePieceSold(p.id, p.sold)}
                          data-testid={`bodega-prod-toggle-${p.id}`}>
                          {p.sold ? "Mark available" : "Mark SOLD"}
                        </button>
                        <button className="btn-mini" onClick={() => deletePiece(p)} data-testid={`bodega-prod-delete-${p.id}`}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        )}

        {tab === "reports" && (
          <section className="admin-section">
            <h2 className="admin-section-title">Reported chat messages ({reports.length})</h2>
            {reports.length === 0 ? (
              <div className="feed-empty">Nothing reported. Vibes are clean.</div>
            ) : (
              <div className="admin-list">
                {reports.map((r) => (
                  <div key={r.id} className="admin-row admin-report" data-testid={`admin-report-${r.id}`}>
                    <div style={{ flex: 1, minWidth: 260 }}>
                      <div className="admin-meta">
                        Reported by <b>{r.reporterEmail || "unknown"}</b> — {new Date(r.ts).toLocaleString()}
                      </div>
                      <div className="admin-name" style={{ fontSize: "1.3rem", marginTop: 4 }}>
                        {r.authorName || r.authorEmail || "author"} said:
                      </div>
                      <div className={`admin-report-text${r.messageDeleted ? " deleted" : ""}`}>
                        {r.messageText || "(empty)"}
                      </div>
                      {r.reason && (
                        <div className="admin-meta" style={{ marginTop: 6 }}>
                          <em>Reason: {r.reason}</em>
                        </div>
                      )}
                    </div>
                    <div className="admin-actions">
                      <button
                        className="btn-ghost"
                        disabled={busyId === r.id}
                        onClick={() => act(r.id, "dismiss", "POST", null, "reports")}
                        data-testid={`report-dismiss-${r.id}`}
                      >
                        <X size={14} /> Dismiss
                      </button>
                      <button
                        className="btn-solid"
                        disabled={busyId === r.id || r.messageDeleted}
                        onClick={() => {
                          if (window.confirm("Delete this message for everyone?")) {
                            act(r.id, "delete-message", "POST", null, "reports");
                          }
                        }}
                        data-testid={`report-delete-${r.id}`}
                      >
                        <Trash2 size={14} /> Delete message
                      </button>
                      <button
                        className="btn-ghost btn-danger"
                        disabled={busyId === r.id}
                        onClick={() => {
                          if (window.confirm(`Ban ${r.authorEmail || 'this user'}? They'll lose posting rights until you approve them again.`)) {
                            act(r.id, "ban-author", "POST", null, "reports");
                          }
                        }}
                        data-testid={`report-ban-${r.id}`}
                      >
                        <Ban size={14} /> Ban author
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        {tab === "access" && (
          <section className="admin-section">
            {countAccess.length === 0 ? (
              <div className="feed-empty">No access requests right now.</div>
            ) : (
              <div className="admin-list">
                {countAccess.map((r) => (
                  <div key={r.id} className="admin-row" data-testid={`admin-access-${r.id}`}>
                    <div className="admin-user" style={{ flex: 1 }}>
                      <div>
                        <div className="admin-name">
                          {r.name}{" "}
                          <span className={`admin-badge status-${r.status}`}>{r.status}</span>
                        </div>
                        <div className="admin-meta">{r.email}</div>
                        <div className="admin-meta">
                          Requested {r.requested_at ? new Date(r.requested_at).toLocaleString() : "recently"}
                        </div>
                      </div>
                    </div>
                    <div className="admin-actions">
                      {r.status !== "approved" && (
                        <button
                          className="btn-solid"
                          disabled={busyId === r.id}
                          onClick={() => act(r.id, "approve", "POST", null, "count-access")}
                          data-testid={`admin-access-approve-${r.id}`}
                        >
                          <Check size={14} /> Approve
                        </button>
                      )}
                      {r.status !== "denied" && (
                        <button
                          className="btn-ghost btn-danger"
                          disabled={busyId === r.id}
                          onClick={() => act(r.id, "deny", "POST", null, "count-access")}
                          data-testid={`admin-access-deny-${r.id}`}
                        >
                          <X size={14} /> Deny
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "applications" && (
          <section className="admin-section">
            <div className="admin-app-filters">
              {["all", "music", "brand", "art"].map((f) => (
                <button
                  key={f}
                  className={appFilter === f ? "chip active" : "chip"}
                  onClick={() => setAppFilter(f)}
                  data-testid={`admin-app-filter-${f}`}
                >
                  {f === "music" && <MusicIcon size={12} />}
                  {f === "brand" && <ShoppingBag size={12} />}
                  {f === "art" && <Palette size={12} />}
                  {f}
                </button>
              ))}
            </div>
            {(() => {
              const list = applications.filter(a => appFilter === "all" || a.kind === appFilter);
              if (list.length === 0) return <div className="feed-empty">Nothing to review.</div>;
              return (
                <div className="admin-list">
                  {list.map((a) => (
                    <div
                      key={a.id}
                      className={`admin-row admin-app admin-app-${a.status}`}
                      data-testid={`admin-app-${a.id}`}
                    >
                      <div className="admin-user" style={{ flex: 1 }}>
                        {a.hero_image_url && (
                          <div className="admin-avatar admin-avatar-lg">
                            <img src={a.hero_image_url} alt="" />
                          </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="admin-name">
                            {a.display_name}{" "}
                            <span className={`admin-badge status-${a.status}`}>
                              {a.kind} · {a.status}
                            </span>
                          </div>
                          <div className="admin-meta">
                            {a.applicant_name || a.applicant_email} · {a.contact_email}
                          </div>
                          {a.tagline && <div className="admin-meta"><i>{a.tagline}</i></div>}
                          <div className="admin-app-pitch">{a.pitch}</div>
                          {a.media?.length > 0 && (
                            <div className="admin-app-media">
                              {a.media.slice(0, 6).map((m, i) => (
                                <div key={i} className={`admin-app-media-tile media-${m.kind}`}>
                                  {m.kind === "image" ? (
                                    <img src={m.url} alt="" />
                                  ) : m.kind === "audio" ? (
                                    <audio src={m.url} controls preload="none" />
                                  ) : m.kind === "video" ? (
                                    <video src={m.url} controls preload="none" />
                                  ) : (
                                    <a href={m.url} target="_blank" rel="noopener noreferrer">file</a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {a.status === "pending" && (
                        <div className="admin-actions">
                          <button
                            className="btn-solid"
                            disabled={busyId === a.id}
                            onClick={() => {
                              const note = window.prompt("Optional note for the applicant (welcome, next steps…)", "") || "";
                              act(a.id, "approve", "POST", { note }, "applications");
                            }}
                            data-testid={`admin-app-approve-${a.id}`}
                          >
                            <Check size={14} /> Approve
                          </button>
                          <button
                            className="btn-ghost btn-danger"
                            disabled={busyId === a.id}
                            onClick={() => {
                              const note = window.prompt("Optional reason (visible to applicant)", "") || "";
                              if (window.confirm("Reject this application?")) {
                                act(a.id, "reject", "POST", { note }, "applications");
                              }
                            }}
                            data-testid={`admin-app-reject-${a.id}`}
                          >
                            <X size={14} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </section>
        )}
        {tab === "shirt" && current?.is_super_admin && (
          <section className="admin-section" data-testid="admin-shirt-section">
            <h2 className="admin-section-title">THE SHIRT reservations ({shirtClaims.length})</h2>
            {shirtClaims.length === 0 ? (
              <div className="feed-empty">No reservations yet.</div>
            ) : (
              <div className="admin-list">
                {shirtClaims.map((c, i) => (
                  <div key={i} className="admin-row" data-testid={`admin-shirt-claim-${i}`}>
                    <div className="admin-user" style={{ flex: 1 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="admin-name">
                          {c.name || c.email || "Guest checkout"}{" "}
                          <span className={`admin-badge ${c.free ? "status-approved" : "status-pending"}`}>
                            {c.free ? "FREE — member" : "PAID $80"}
                          </span>
                        </div>
                        <div className="admin-meta">
                          Size {c.size} · qty {c.quantity} · {c.email}
                          {c.created_at ? ` · ${new Date(c.created_at).toLocaleDateString()}` : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        {tab === "requests" && (
          <section className="admin-section" data-testid="admin-requests-section">
            {["studio", "consulting", "photoshoot", "artshow", "photography", "svcconsult", "website", "graphic", "fashiondesign", "modelcasting", "gamedev", "gamedevteam"].map((kind) => (
              <div key={kind} style={{ marginBottom: "2rem" }}>
                <h2 className="admin-section-title">
                  {{ studio: "Studio sessions", consulting: "Consulting meetings", photoshoot: "Photoshoot bookings", artshow: "Art show uploads", photography: "Photography bookings", svcconsult: "Service consulting", website: "Website design", graphic: "Graphic design", fashiondesign: "Fashion design", modelcasting: "Model casting", gamedev: "Game dev help", gamedevteam: "GameDev team applications" }[kind]}
                  {" "}({(requestsData[kind] || []).length})
                </h2>
                {(requestsData[kind] || []).length === 0 ? (
                  <div className="feed-empty">None yet.</div>
                ) : (
                  <div className="admin-list">
                    {(requestsData[kind] || []).map((r) => (
                      <div key={r.id} className="admin-row" data-testid={`admin-req-${kind}-${r.id}`}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="admin-name">
                            {r.name || r.full_name}{" "}
                            <span className={`admin-badge ${r.status === "approved" ? "status-approved" : "status-pending"}`}>{r.status}</span>
                            {r.paid && <span className="admin-badge status-approved" data-testid={`req-paid-${r.id}`}>PAID{r.paid_amount ? ` $${r.paid_amount}` : ""}</span>}
                          </div>
                          <div className="admin-meta">
                            {r.email}
                            {r.purpose ? ` · ${r.purpose}` : ""}
                            {r.location ? ` · ${r.location}` : ""}
                            {r.date ? ` · ${r.date} ${r.time || ""}${r.hours ? ` (${r.hours}h)` : ""}` : ""}
                            {r.phone ? ` · ${r.phone}` : ""}
                            {r.people ? ` · ${r.people} people` : ""}
                            {r.shoot_studio || r.shoot_outdoor ? ` · ${[r.shoot_studio && "studio", r.shoot_outdoor && "outdoor"].filter(Boolean).join(" + ")} shoot` : ""}
                            {r.backdrop ? ` · backdrop: ${r.backdrop_color}` : ""}
                            {r.top_size ? ` · sizes: top ${r.top_size} / bottom ${r.bottom_size} / shoe ${r.shoe_size}` : ""}
                            {r.availability ? ` · available: ${r.availability}` : ""}
                            {r.preferred_date ? ` · prefers ${r.preferred_date} ${r.preferred_time || ""}` : ""}
                            {r.instagram ? ` · ${r.instagram}` : ""}
                            {r.provider_name ? ` · booked: ${r.provider_name} ($${r.provider_price}${r.provider_unit === "hour" ? "/hr" : " flat"})` : ""}
                            {Array.isArray(r.services_needed) && r.services_needed.length > 0 ? ` · needs: ${r.services_needed.join(", ")}` : ""}
                            {r.rescheduled_date ? ` · moved to ${r.rescheduled_date} ${r.rescheduled_time}` : ""}
                          </div>
                          {(r.details || r.bio || r.equipment || r.concept || r.notes || r.blurb || r.scope || r.description || r.topic || r.about) && (
                            <div className="admin-meta">{r.details || r.bio || r.concept || r.blurb || r.scope || r.description || r.topic || r.about || ""} {r.equipment ? `· gear: ${r.equipment}` : ""} {r.notes ? `· ${r.notes}` : ""}</div>
                          )}
                          {(r.upload_url || r.concept_url || r.portfolio_url) && (
                            <a className="music-ig" href={r.upload_url || r.concept_url || r.portfolio_url} target="_blank" rel="noopener noreferrer">view work</a>
                          )}
                        </div>
                        {kind !== "artshow" || true ? (
                          <div className="admin-actions">
                            <button className="btn-mini approve" onClick={() => decideRequest(kind, r.id, "approved")} data-testid={`req-approve-${r.id}`}><Check size={13} /> Approve</button>
                            <button className="btn-mini danger" onClick={() => decideRequest(kind, r.id, "declined")} data-testid={`req-decline-${r.id}`}><X size={13} /> Decline</button>
                            {kind !== "artshow" && (
                              <button className="btn-mini" onClick={() => decideRequest(kind, r.id, "rescheduled")} data-testid={`req-reschedule-${r.id}`}>Reschedule</button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <h2 className="admin-section-title">Excourtier pre-orders ({(requestsData.preorders || []).length})</h2>
            {(requestsData.preorders || []).length === 0 ? (
              <div className="feed-empty">No sign-ups yet.</div>
            ) : (
              <div className="admin-list">
                {(requestsData.preorders || []).map((r) => (
                  <div key={r.id} className="admin-row" data-testid={`admin-preorder-${r.id}`}>
                    <div style={{ flex: 1 }}>
                      <div className="admin-name">{r.full_name} — size {r.size} {r.wants_model && <span className="admin-badge status-approved">WANTS TO MODEL</span>}</div>
                      <div className="admin-meta">{r.email} · {r.phone} {r.instagram ? `· ${r.instagram}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "services" && (
          <section className="admin-section" data-testid="admin-services-section">
            <h2 className="admin-section-title">Assign a bookable member</h2>
            <div className="svc-assign-row">
              <select className="req-input" value={svcDraft.user_id}
                onChange={(e) => setSvcDraft({ ...svcDraft, user_id: e.target.value })}
                data-testid="svc-assign-member">
                <option value="">Pick a member…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
              <select className="req-input" value={svcDraft.service_type}
                onChange={(e) => setSvcDraft({ ...svcDraft, service_type: e.target.value })}
                data-testid="svc-assign-type">
                {providerTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className="req-input" type="number" min="0" step="0.01" placeholder="Price $"
                value={svcDraft.price_amount}
                onChange={(e) => setSvcDraft({ ...svcDraft, price_amount: e.target.value })}
                data-testid="svc-assign-price" />
              <select className="req-input" value={svcDraft.price_unit}
                onChange={(e) => setSvcDraft({ ...svcDraft, price_unit: e.target.value })}
                data-testid="svc-assign-unit">
                <option value="hour">per hour</option>
                <option value="flat">flat fee</option>
              </select>
              <input className="req-input svc-fee-input" type="number" min="0.5" step="0.01" placeholder="Booking fee $"
                title="Booking fee charged through Stripe when a booking is approved"
                value={svcDraft.booking_fee}
                onChange={(e) => setSvcDraft({ ...svcDraft, booking_fee: e.target.value })}
                data-testid="svc-assign-booking-fee" />
              <button className="btn-mini approve" onClick={addProvider}
                disabled={!svcDraft.user_id || svcDraft.price_amount === ""}
                data-testid="svc-assign-add"><Check size={13} /> Assign</button>
            </div>
            <h2 className="admin-section-title" style={{ marginTop: "1.6rem" }}>Bookable members ({providers.length})</h2>
            {providers.length === 0 ? (
              <div className="feed-empty">No members assigned yet.</div>
            ) : (
              <div className="admin-list">
                {providers.map((p) => (
                  <div key={p.id} className="admin-row" data-testid={`svc-provider-${p.id}`}>
                    <div style={{ flex: 1 }}>
                      <div className="admin-name">{p.name} <span className="admin-badge status-approved">{p.service_type}</span></div>
                      <div className="admin-meta">
                        ${Number(p.price_amount).toLocaleString()}{p.price_unit === "hour" ? " / hour" : " flat fee"}
                        {p.booking_fee ? ` · $${Number(p.booking_fee).toLocaleString()} booking fee` : " · no booking fee set"}
                      </div>
                    </div>
                    <div className="admin-actions">
                      <button className="btn-mini danger" onClick={() => removeProvider(p.id)} data-testid={`svc-provider-remove-${p.id}`}><Trash2 size={13} /> Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "casting" && (
          <section className="admin-section" data-testid="admin-casting-section">
            <h2 className="admin-section-title">Approved model submissions</h2>
            {(requestsData.modelcasting || []).filter((r) => r.status === "approved").length === 0 ? (
              <div className="feed-empty">No approved model submissions yet. Approve them in the Requests tab.</div>
            ) : (
              <div className="casting-gallery">
                {(requestsData.modelcasting || []).filter((r) => r.status === "approved").map((r) => (
                  <div className="casting-card" key={r.id} data-testid={`casting-card-${r.id}`}>
                    {r.portfolio_url ? (
                      <a href={r.portfolio_url} target="_blank" rel="noopener noreferrer">
                        <img src={r.portfolio_url} alt={r.name} className="casting-photo"
                          onError={(e) => { e.target.style.display = "none"; e.target.parentNode.classList.add("casting-nophoto"); }} />
                      </a>
                    ) : (
                      <div className="casting-photo casting-nophoto">no portfolio</div>
                    )}
                    <div className="casting-info">
                      <div className="casting-name">{r.name}</div>
                      <div className="casting-sizes">
                        <span>top <b>{r.top_size || "—"}</b></span>
                        <span>btm <b>{r.bottom_size || "—"}</b></span>
                        <span>shoe <b>{r.shoe_size || "—"}</b></span>
                      </div>
                      {r.availability && <div className="casting-avail">{r.availability}</div>}
                      <div className="casting-contact">{r.instagram || r.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "activity" && activity && (
          <section className="admin-section" data-testid="admin-activity-section">
            {current?.is_super_admin && (
              <>
                <h2 className="admin-section-title">Website manager</h2>
                <div className="admin-meta" style={{ marginBottom: "0.6rem" }}>
                  Current: {(activity.website_managers || []).map((m) => m.name || m.email).join(", ") || "none assigned"}
                </div>
                <div className="admin-list" style={{ marginBottom: "2rem" }}>
                  {users.filter((u) => u.status === "approved").map((u) => {
                    const isMgr = (activity.website_managers || []).some((m) => m.user_id === u.id);
                    return (
                      <div key={u.id} className="admin-row">
                        <div style={{ flex: 1 }}>
                          <div className="admin-name">{u.name}</div>
                          <div className="admin-meta">{u.email}</div>
                        </div>
                        <button className={`btn-mini ${isMgr ? "danger" : "approve"}`} data-testid={`wm-toggle-${u.id}`}
                          onClick={() => setWebsiteManager(u.id, !isMgr)}>
                          {isMgr ? "Remove manager" : "Make website manager"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            <h2 className="admin-section-title">Latest chat activity</h2>
            <div className="admin-list" style={{ marginBottom: "2rem" }}>
              {(activity.chats || []).map((c, i) => (
                <div key={i} className="admin-row">
                  <div style={{ flex: 1 }}>
                    <div className="admin-name">{c.author}</div>
                    <div className="admin-meta">{c.text} {c.ts ? `· ${new Date(c.ts).toLocaleString()}` : ""}</div>
                  </div>
                </div>
              ))}
              {(activity.chats || []).length === 0 && <div className="feed-empty">No chat messages yet.</div>}
            </div>
            <h2 className="admin-section-title">Member room changes</h2>
            <div className="admin-list">
              {(activity.members || []).map((m, i) => (
                <div key={i} className="admin-row">
                  <div style={{ flex: 1 }}>
                    <div className="admin-name">{m.name} <span className={`admin-badge ${m.status === "approved" ? "status-approved" : "status-pending"}`}>{m.status}</span></div>
                    <div className="admin-meta">{m.email} · joined {m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default AdminPanel;
